// utils/validators/mediaValidator.ts

import {
  normalizeBaseMime,
  isAllowedAudioMime, isAllowedAudioExt,
  isAllowedVideoMime, isAllowedVideoExt,
  isAllowedImageMime, isAllowedImageExt,
} from "../mediaTools/mimeUtils";

export type MediaKind = "image" | "audio" | "video";
export type Tier = "S" | "M" | "L"; // small / medium / large

export type FileLimits = {
  maxMB: number;
  maxPixels?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  maxDurationSec?: number; // for audio/video
};

export type ValidateOptions = {
  useEnv?: boolean;
  override?: Partial<Record<MediaKind, Partial<Record<Tier, Partial<FileLimits>>>>>;
  defaultTier?: Tier;
};

/* ------------------------------
   Result Types + Type Guards
------------------------------ */
export type SuccessResult = {
  ok: true;
  kind: MediaKind;
  tier: Tier;
  sizeMB: number;
  details?: Record<string, any>;
};

export type ErrorResult = {
  ok: false;
  kind: MediaKind;
  tier: Tier;
  code: string;
  message: string;
  details?: Record<string, any>;
};

export type ValidateResult = SuccessResult | ErrorResult;

export function isErrorResult(r: ValidateResult): r is ErrorResult {
  return r.ok === false;
}
export function isSuccessResult(r: ValidateResult): r is SuccessResult {
  return r.ok === true;
}

/* ------------------------------
   Env Helpers
------------------------------ */
const toNum = (v: any, fallback: number) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

function envPreset(kind: MediaKind): Record<Tier, FileLimits> {
  if (kind === "image") {
    return {
      S: { maxMB: toNum(process.env.NEXT_PUBLIC_MEDIA_IMAGE_MB_S, 2) },
      M: { maxMB: toNum(process.env.NEXT_PUBLIC_MEDIA_IMAGE_MB_M, 8) },
      L: { maxMB: toNum(process.env.NEXT_PUBLIC_MEDIA_IMAGE_MB_L, 20) },
    };
  }
  if (kind === "audio") {
    return {
      S: { maxMB: toNum(process.env.NEXT_PUBLIC_MEDIA_AUDIO_MB_S, 10) },
      M: { maxMB: toNum(process.env.NEXT_PUBLIC_MEDIA_AUDIO_MB_M, 30) },
      L: { maxMB: toNum(process.env.NEXT_PUBLIC_MEDIA_AUDIO_MB_L, 50) },
    };
  }
  // video
  return {
    S: { maxMB: toNum(process.env.NEXT_PUBLIC_MEDIA_VIDEO_MB_S, 150) },
    M: { maxMB: toNum(process.env.NEXT_PUBLIC_MEDIA_VIDEO_MB_M, 300) },
    L: { maxMB: toNum(process.env.NEXT_PUBLIC_MEDIA_VIDEO_MB_L, 500) },
  };
}

function mergeLimits(base: FileLimits, override?: Partial<FileLimits>): FileLimits {
  return { ...base, ...(override || {}) };
}

function getLimits(kind: MediaKind, tier: Tier, opts?: ValidateOptions): FileLimits {
  const useEnv = opts?.useEnv !== false;
  const base = useEnv ? envPreset(kind)[tier] : { maxMB: Number.POSITIVE_INFINITY };
  const ovKind = opts?.override?.[kind]?.[tier];
  return mergeLimits(base, ovKind);
}

/* ------------------------------
   MIME/EXT allowlist via mimeUtils
------------------------------ */
function mimeExtOk(kind: MediaKind, mime: string, ext: string): boolean {
  const base = normalizeBaseMime(mime);
  if (kind === "image") return isAllowedImageMime(base) || isAllowedImageExt(ext);
  if (kind === "audio") return isAllowedAudioMime(base) || isAllowedAudioExt(ext);
  return isAllowedVideoMime(base) || isAllowedVideoExt(ext);
}

/* ------------------------------
   Single File Validation
------------------------------ */
export async function validateSingleFile(
  file: File | null,
  kind: MediaKind,
  tier: Tier = "M",
  opts?: ValidateOptions
): Promise<ValidateResult> {
  const chosenTier = tier ?? (opts?.defaultTier ?? "M");
  if (!file) {
    return { ok: false, kind, tier: chosenTier, code: "NO_FILE", message: "Please choose a file." };
  }

  const limits = getLimits(kind, chosenTier, opts);
  const mime = normalizeBaseMime(file.type || "");
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const sizeMB = file.size / (1024 * 1024);

  if (!mimeExtOk(kind, mime, ext)) {
    return {
      ok: false,
      kind,
      tier: chosenTier,
      code: "BAD_FORMAT",
      message: kind === "image" ? "Unsupported image format (PNG/JPG/WebP/etc.)." : `Unsupported ${kind} format.`,
      details: { mime, ext },
    };
  }

  if (limits.maxMB && sizeMB > limits.maxMB) {
    return {
      ok: false,
      kind,
      tier: chosenTier,
      code: "TOO_LARGE",
      message: `File too large. Max ${limits.maxMB}MB.`,
      details: { sizeMB },
    };
  }

  return { ok: true, kind, tier: chosenTier, sizeMB };
}

/* ------------------------------
   Media Probes (client-side)
------------------------------ */

/**
 * Returns media duration in seconds.
 * If the browser can't decode (unsupported codec), resolves to NaN (does NOT throw).
 */
export async function getMediaDuration(file: File): Promise<number> {
  const url = URL.createObjectURL(file);
  const baseMime = normalizeBaseMime(file.type || "");
  const useAudioEl = baseMime.startsWith("audio/");

  try {
    return await new Promise<number>((resolve) => {
      const el = document.createElement(useAudioEl ? "audio" : "video");
      el.preload = "metadata";
      el.onloadedmetadata = () => {
        const d = Number.isFinite(el.duration) ? el.duration : Number.NaN;
        resolve(d);
        URL.revokeObjectURL(url);
      };
      el.onerror = () => {
        resolve(Number.NaN); // unsupported -> let backend enforce
        URL.revokeObjectURL(url);
      };
      el.src = url;
    });
  } catch {
    URL.revokeObjectURL(url);
    return Number.NaN;
  }
}

/* ------------------------------
   Optional Rules
------------------------------ */
export async function enforceImageGeometry(
  file: File,
  limits: Pick<FileLimits, "minWidth" | "minHeight" | "maxWidth" | "maxHeight" | "maxPixels">,
  ctx: { kind: MediaKind; tier: Tier }
): Promise<ErrorResult | null> {
  const { width, height } = await getImageSize(file);
  const pixels = width * height;

  if (limits.minWidth && width < limits.minWidth)
    return { ok: false, kind: ctx.kind, tier: ctx.tier, code: "IMG_TOO_NARROW", message: `Image width must be ≥ ${limits.minWidth}px.`, details: { width, height } };
  if (limits.minHeight && height < limits.minHeight)
    return { ok: false, kind: ctx.kind, tier: ctx.tier, code: "IMG_TOO_SHORT", message: `Image height must be ≥ ${limits.minHeight}px.`, details: { width, height } };
  if (limits.maxWidth && width > limits.maxWidth)
    return { ok: false, kind: ctx.kind, tier: ctx.tier, code: "IMG_TOO_WIDE", message: `Image width must be ≤ ${limits.maxWidth}px.`, details: { width, height } };
  if (limits.maxHeight && height > limits.maxHeight)
    return { ok: false, kind: ctx.kind, tier: ctx.tier, code: "IMG_TOO_TALL", message: `Image height must be ≤ ${limits.maxHeight}px.`, details: { width, height } };
  if (limits.maxPixels && pixels > limits.maxPixels)
    return { ok: false, kind: ctx.kind, tier: ctx.tier, code: "IMG_TOO_MANY_PIXELS", message: `Image is too large in pixels.`, details: { width, height, pixels } };

  return null;
}

export async function getImageSize(file: File): Promise<{ width: number; height: number }> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = (e) => reject(e);
      i.src = url;
    });
    return { width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * If the browser can’t decode (duration = NaN), do not block on the client.
 * Let the backend (ffprobe/ffmpeg) enforce max duration.
 */
export async function enforceDuration(
  file: File,
  maxDurationSec: number,
  ctx: { kind: MediaKind; tier: Tier }
): Promise<ErrorResult | null> {
  const dur = await getMediaDuration(file);

  if (!Number.isFinite(dur)) {
    return null; // skip on client if unsupported/undetectable
  }

  if (dur > maxDurationSec) {
    return {
      ok: false,
      kind: ctx.kind,
      tier: ctx.tier,
      code: "TOO_LONG",
      message: `Max duration is ${Math.round(maxDurationSec / 60)} minutes.`,
      details: { durationSec: dur },
    };
  }
  return null;
}

/* ------------------------------
   Bundle Validation
------------------------------ */
export type BundleSpec = {
  [key: string]: { file: File | null; kind: MediaKind; tier?: Tier; extra?: Partial<FileLimits> };
};

export async function validateBundle(
  spec: BundleSpec,
  options?: ValidateOptions
): Promise<{ ok: true } | { ok: false; errors: Record<string, ErrorResult> }> {
  const errs: Record<string, ErrorResult> = {};

  for (const key of Object.keys(spec)) {
    const { file, kind, tier = options?.defaultTier ?? "M", extra } = spec[key];

    // 1) basic checks
    const base = await validateSingleFile(file, kind, tier, options);
    if (isErrorResult(base)) {
      errs[key] = base;
      continue;
    }

    // 2) optional rules
    if (kind === "image" && extra) {
      const geoFail = await enforceImageGeometry(
        file as File,
        {
          minWidth: extra.minWidth,
          minHeight: extra.minHeight,
          maxWidth: extra.maxWidth,
          maxHeight: extra.maxHeight,
          maxPixels: extra.maxPixels,
        },
        { kind, tier }
      );
      if (geoFail) {
        errs[key] = geoFail;
        continue;
      }
    }

    if ((kind === "audio" || kind === "video") && extra?.maxDurationSec) {
      const durFail = await enforceDuration(file as File, extra.maxDurationSec, { kind, tier });
      if (durFail) {
        errs[key] = durFail;
        continue;
      }
    }
  }

  if (Object.keys(errs).length) return { ok: false, errors: errs };
  return { ok: true };
}
